import axios, { AxiosInstance, AxiosResponse } from 'axios'
import type {
  ApiResponse,
  PaginatedResponse,
  Lesson,
  User,
  LessonActivity,
  PlayerResponse,
  AnalyticsData,
  OrganizationOverview,
  LessonOverview,
  DetailedPlayerAnalytics,
  RealtimeLessonData,
  ShellCommandResult,
  Course,
  CourseRun,
  CourseProgress,
  DashboardData,
  ReferralInfo,
  LeaderboardData,
  SelfPacedDashboardData,
  CourseOverview,
  CourseRunsAnalytics,
  CourseRunOverview,
  CourseStudent,
  CyberCard,
} from '@/types'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: '/api/v1',
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    })

    // Request interceptor for authentication
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Add CSRF token for Rails
        const csrfToken = this.getCsrfToken()
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken
        }

        // Let axios set multipart/form-data boundary automatically for FormData
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = error.config?.url || ''
          // Don't redirect for auth endpoints — let the login/password form handle the error
          const isAuthRequest = url.startsWith('/auth/')

          if (!isAuthRequest) {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('cached_user')
            window.location.href = '/auth'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token')
  }

  private getCsrfToken(): string | null {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag?.getAttribute('content') || null
  }

  // Authentication
  async login(email: string, password: string, captchaToken?: string, rememberMe?: boolean): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.client.post('/auth/sign_in', { email, password, captcha_token: captchaToken, remember_me: rememberMe }, { timeout: 3000 })
    return response.data
  }

  async signIn(credentials: { email: string; password: string; captcha_token?: string }): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.client.post('/auth/sign_in', credentials, { timeout: 3000 })
    return response.data
  }

  async signUp(userData: {
    email: string;
    password: string;
    password_confirmation: string;
    first_name: string;
    last_name: string;
    referral_code?: string;
  }, captchaToken?: string): Promise<ApiResponse<{ message: string; user_id: string }>> {
    const response = await this.client.post('/auth/sign_up', { ...userData, captcha_token: captchaToken })
    return response.data
  }

  async logout(): Promise<void> {
    await this.client.delete('/auth/sign_out')
    localStorage.removeItem('auth_token')
  }

  async verifyEmail(token: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.post('/auth/email/verify', { token })
    return response.data
  }

  async resendVerification(email?: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.post('/auth/email/verify/resend', email ? { email } : {})
    return response.data
  }

  async initiatePasswordReset(email: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.post('/auth/password/reset', { email })
    return response.data
  }

  async confirmPasswordReset(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.post('/auth/password/reset/confirm', { token, password })
    return response.data
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    // Use shorter timeout for auth checks to fail faster
    const response = await this.client.get('/auth/me', { timeout: 3000 })
    return response.data
  }


  async getRunInfo(runCode: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/public/run/${runCode}/info`)
    return response.data
  }

  async getLesson(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/lessons/${id}`)
    return response.data
  }

  async updateLesson(id: string, lessonData: any): Promise<ApiResponse<Lesson>> {
    const response = await this.client.patch(`/lessons/${id}`, lessonData)
    return response.data
  }

  // Public lesson access (no auth required) — via run_code
  async joinRunPublic(runCode: string, displayName: string, sessionId?: string): Promise<ApiResponse<{
    player: any;
    lesson: Lesson;
    session_id: string;
    reconnected: boolean;
  }>> {
    const response = await this.client.post(`/public/run/${runCode}/join`,
      { display_name: displayName },
      {
        headers: sessionId ? { 'X-Session-ID': sessionId } : {},
        timeout: 5000
      }
    )
    return response.data
  }

  async getRunStatePublic(runCode: string, sessionId: string): Promise<ApiResponse<{
    lesson: Lesson;
    player: any;
    current_activity: LessonActivity | null;
    show_pdf?: boolean;
  }>> {
    const response = await this.client.get(`/public/run/${runCode}/state`, {
      headers: { 'X-Session-ID': sessionId }
    })
    return response.data
  }

  async executeShellCommand(runCode: string, sessionId: string, activityId: string, command: string): Promise<ApiResponse<ShellCommandResult>> {
    const response = await this.client.post(
      `/public/run/${runCode}/execute_command`,
      { activity_id: activityId, command },
      { headers: { 'X-Session-ID': sessionId }, timeout: 20000 }
    )
    return response.data
  }

  async submitResponsePublic(runCode: string, sessionId: string, activityId: string, responseData: any): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/public/run/${runCode}/respond`,
      { activity_id: activityId, response_data: responseData },
      { headers: { 'X-Session-ID': sessionId } }
    )
    return response.data
  }

  async getLessonResultsPublic(runCode: string, sessionId: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/public/run/${runCode}/results`, {
      headers: { 'X-Session-ID': sessionId }
    })
    return response.data
  }

  async getPlayerInfo(runCode: string, sessionId: string): Promise<ApiResponse<{
    player: any;
    lesson: Lesson;
    session_valid: boolean;
  }>> {
    const response = await this.client.get(`/public/player/${sessionId}`, {
      params: { run_code: runCode }
    })
    return response.data
  }

  // Lesson control actions
  async startLesson(id: string, force = false): Promise<ApiResponse<Lesson>> {
    const response = await this.client.post(`/lessons/${id}/start`, null, force ? { params: { force: 'true' } } : undefined)
    return response.data
  }

  async pauseLesson(id: string): Promise<ApiResponse<Lesson>> {
    const response = await this.client.post(`/lessons/${id}/pause`)
    return response.data
  }

  async resumeLesson(id: string): Promise<ApiResponse<Lesson>> {
    const response = await this.client.post(`/lessons/${id}/resume`)
    return response.data
  }

  async completeLesson(id: string): Promise<ApiResponse<Lesson>> {
    const response = await this.client.post(`/lessons/${id}/complete`)
    return response.data
  }

  async getLessonState(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/lessons/${id}/state`)
    return response.data
  }

  async getSelfPacedDashboard(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/lessons/${id}/self_paced_dashboard`)
    return response.data
  }

  async exportLessonResults(id: string, format: string = 'csv'): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/lessons/${id}/export_results`, {
      params: { format }
    })
    return response.data
  }

  // Player management
  async getLessonPlayers(id: string): Promise<ApiResponse<any[]>> {
    const response = await this.client.get(`/lessons/${id}/players`)
    return response.data
  }

  async kickPlayer(lessonId: string, playerId: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/lessons/${lessonId}/players/${playerId}`)
    return response.data
  }

  async bulkPlayerAction(lessonId: string, action: string, playerIds: string[]): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/lessons/${lessonId}/players/bulk_action`, {
      action,
      player_ids: playerIds
    })
    return response.data
  }

  // Lesson Activities
  private activityPath(lessonId: string, suffix = ''): string {
    return `/lessons/${lessonId}/activities${suffix}`
  }

  async getLessonActivities(lessonId: string): Promise<ApiResponse<LessonActivity[]>> {
    const response = await this.client.get(this.activityPath(lessonId))
    return response.data
  }

  async createActivity(lessonId: string, activityData: any): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId), activityData)
    return response.data
  }

  async updateActivity(lessonId: string, activityId: string, activityData: any): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.patch(this.activityPath(lessonId, `/${activityId}`), activityData)
    return response.data
  }

  async reorderActivities(lessonId: string, activityIds: string[]): Promise<ApiResponse<LessonActivity[]>> {
    const response = await this.client.patch(this.activityPath(lessonId, '/reorder'), { activity_ids: activityIds })
    return response.data
  }

  async deleteActivity(lessonId: string, activityId: string): Promise<void> {
    await this.client.delete(this.activityPath(lessonId, `/${activityId}`))
  }

  async startActivity(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId, `/${activityId}/start`))
    return response.data
  }

  async pauseActivity(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId, `/${activityId}/pause`))
    return response.data
  }

  async resumeActivity(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId, `/${activityId}/resume`))
    return response.data
  }

  async completeActivity(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId, `/${activityId}/complete`))
    return response.data
  }

  async skipActivity(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId, `/${activityId}/skip`))
    return response.data
  }

  // Timer controls for activities
  async extendActivityTime(lessonId: string, activityId: string, minutes: number): Promise<ApiResponse<LessonActivity>> {
    const response = await this.client.post(this.activityPath(lessonId, `/${activityId}/extend_time`), { minutes })
    return response.data
  }

  // Host shell command execution (authenticated)
  async executeShellCommandHost(lessonId: string, activityId: string, command: string): Promise<ApiResponse<ShellCommandResult>> {
    const response = await this.client.post(
      this.activityPath(lessonId, `/${activityId}/execute_command`),
      { command },
      { timeout: 20000 }
    )
    return response.data
  }

  // Player Responses
  async submitResponse(lessonId: string, activityId: string, responseData: Record<string, any>): Promise<ApiResponse<PlayerResponse>> {
    const response = await this.client.post(`/lessons/${lessonId}/activities/${activityId}/responses`, { response_data: responseData })
    return response.data
  }

  async getResponses(lessonId: string, activityId: string): Promise<ApiResponse<PlayerResponse[]>> {
    const response = await this.client.get(`/lessons/${lessonId}/activities/${activityId}/responses`)
    return response.data
  }

  // Analytics
  async getLessonAnalytics(lessonId: string): Promise<ApiResponse<AnalyticsData>> {
    const response = await this.client.get(`/lessons/${lessonId}/analytics`)
    return response.data
  }

  async getActivityAnalytics(lessonId: string, activityId: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/lessons/${lessonId}/activities/${activityId}/analytics`)
    return response.data
  }

  // New Analytics Endpoints
  async getOrganizationOverview(organizationId: string): Promise<ApiResponse<OrganizationOverview>> {
    const response = await this.client.get(`/analytics/organization/${organizationId}/overview`)
    return response.data
  }

  async getLessons(params?: { page?: number }): Promise<PaginatedResponse<Lesson>> {
    const response = await this.client.get('/lessons', { params })
    return response.data
  }

  async getLessonOverview(lessonId: string): Promise<ApiResponse<LessonOverview>> {
    const response = await this.client.get(`/analytics/lesson/${lessonId}/overview`)
    return response.data
  }

  async getLessonActivitiesAnalytics(lessonId: string): Promise<ApiResponse<any[]>> {
    const response = await this.client.get(`/analytics/lesson/${lessonId}/activities`)
    return response.data
  }

  async getLessonPlayersAnalytics(lessonId: string): Promise<ApiResponse<DetailedPlayerAnalytics[]>> {
    const response = await this.client.get(`/analytics/lesson/${lessonId}/players`)
    return response.data
  }

  async getRealtimeData(lessonId: string): Promise<ApiResponse<RealtimeLessonData>> {
    const response = await this.client.get(`/analytics/realtime/${lessonId}`)
    return response.data
  }

  // Course Analytics
  async getCourseOverview(courseId: string): Promise<ApiResponse<CourseOverview>> {
    const response = await this.client.get(`/analytics/course/${courseId}/overview`)
    return response.data
  }

  async getCourseRunsAnalytics(courseId: string): Promise<ApiResponse<CourseRunsAnalytics>> {
    const response = await this.client.get(`/analytics/course/${courseId}/runs`)
    return response.data
  }

  async getCourseRunOverview(courseId: string, runId: string): Promise<ApiResponse<CourseRunOverview>> {
    const response = await this.client.get(`/analytics/course/${courseId}/runs/${runId}/overview`)
    return response.data
  }

  async getCourseStudents(courseId: string, page?: number): Promise<ApiResponse<{ students: CourseStudent[]; meta: { page: number; totalPages: number; totalCount: number } }>> {
    const response = await this.client.get(`/analytics/course/${courseId}/students`, { params: { page } })
    return response.data
  }

  // Organizations
  async getUserOrganizations(params?: { search?: string }): Promise<ApiResponse<{ organizations: any[] }>> {
    const response = await this.client.get('/organizations', { params })
    return response.data
  }

  async createOrganization(orgData: { name: string; description?: string; website?: string }): Promise<ApiResponse<any>> {
    const response = await this.client.post('/organizations', orgData)
    return response.data
  }

  async getOrganizationMembers(orgId: string): Promise<ApiResponse<any[]>> {
    const response = await this.client.get(`/organizations/${orgId}/members`)
    return response.data
  }

  // Lesson CRUD
  async createLesson(lessonData: any): Promise<ApiResponse<any>> {
    const response = await this.client.post('/lessons', lessonData)
    return response.data
  }

  async deleteLesson(lessonId: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/lessons/${lessonId}`)
    return response.data
  }

  // File uploads
  async uploadFile(file: File, type: 'image' | 'video' | 'document'): Promise<ApiResponse<{ url: string; key: string }>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)

    const response = await this.client.post('/uploads', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // PDF Slideshow helpers
  getSlideUrl(lessonId: string, activityId: string, page: number): string {
    return `/api/v1/lessons/${lessonId}/activities/${activityId}/slides?page=${page}`
  }

  async uploadActivityPdf(lessonId: string, activityId: string, file: File): Promise<ApiResponse<LessonActivity>> {
    const formData = new FormData()
    formData.append('activity[pdf_file]', file)
    const response = await this.client.patch(
      `/lessons/${lessonId}/activities/${activityId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  async uploadActivityImage(lessonId: string, activityId: string, file: File): Promise<ApiResponse<LessonActivity>> {
    const formData = new FormData()
    formData.append('activity[question_image]', file)
    const response = await this.client.patch(
      `/lessons/${lessonId}/activities/${activityId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  async deleteActivityImage(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const formData = new FormData()
    formData.append('activity[remove_question_image]', 'true')
    const response = await this.client.patch(
      `/lessons/${lessonId}/activities/${activityId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  async uploadExplanationImage(lessonId: string, activityId: string, file: File): Promise<ApiResponse<LessonActivity>> {
    const formData = new FormData()
    formData.append('activity[explanation_image]', file)
    const response = await this.client.patch(
      `/lessons/${lessonId}/activities/${activityId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  async deleteExplanationImage(lessonId: string, activityId: string): Promise<ApiResponse<LessonActivity>> {
    const formData = new FormData()
    formData.append('activity[remove_explanation_image]', 'true')
    const response = await this.client.patch(
      `/lessons/${lessonId}/activities/${activityId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  }

  // Courses
  async getCourses(organizationId: string): Promise<ApiResponse<Course[]>> {
    const response = await this.client.get(`/organizations/${organizationId}/courses`)
    return response.data
  }

  async createCourse(organizationId: string, courseData: { name: string; description?: string }): Promise<ApiResponse<Course>> {
    const response = await this.client.post(`/organizations/${organizationId}/courses`, { course: courseData })
    return response.data
  }

  async getCourse(organizationId: string, courseId: string): Promise<ApiResponse<Course>> {
    const response = await this.client.get(`/organizations/${organizationId}/courses/${courseId}`)
    return response.data
  }

  async updateCourse(organizationId: string, courseId: string, courseData: Partial<Course>): Promise<ApiResponse<Course>> {
    const response = await this.client.patch(`/organizations/${organizationId}/courses/${courseId}`, { course: courseData })
    return response.data
  }

  async deleteCourse(organizationId: string, courseId: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/organizations/${organizationId}/courses/${courseId}`)
    return response.data
  }

  async runCourse(organizationId: string, courseId: string): Promise<ApiResponse<Course>> {
    const response = await this.client.post(`/organizations/${organizationId}/courses/${courseId}/run`)
    return response.data
  }

  async createCourseRun(organizationId: string, courseId: string, title: string, studentTier?: number): Promise<ApiResponse<CourseRun>> {
    const response = await this.client.post(`/organizations/${organizationId}/courses/${courseId}/create_run`, {
      title,
      student_tier: studentTier
    })
    return response.data
  }

  async getMyRuns(organizationId: string, courseId: string): Promise<ApiResponse<CourseRun[]>> {
    const response = await this.client.get(`/organizations/${organizationId}/courses/${courseId}/my_runs`)
    return response.data
  }

  async updateCourseRun(organizationId: string, courseId: string, runId: string, title: string): Promise<ApiResponse<CourseRun>> {
    const response = await this.client.patch(`/organizations/${organizationId}/courses/${courseId}/runs/${runId}`, { title })
    return response.data
  }

  async deleteCourseRun(organizationId: string, courseId: string, runId: string): Promise<ApiResponse<null>> {
    const response = await this.client.delete(`/organizations/${organizationId}/courses/${courseId}/runs/${runId}`)
    return response.data
  }

  async addLessonToCourse(organizationId: string, courseId: string, lessonId: string): Promise<ApiResponse<Course>> {
    const response = await this.client.post(`/organizations/${organizationId}/courses/${courseId}/lessons`, { lesson_id: lessonId })
    return response.data
  }

  async removeLessonFromCourse(organizationId: string, courseId: string, lessonId: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/organizations/${organizationId}/courses/${courseId}/lessons/${lessonId}`)
    return response.data
  }

  async reorderCourseLessons(organizationId: string, courseId: string, lessonIds: string[]): Promise<ApiResponse<any>> {
    const response = await this.client.patch(`/organizations/${organizationId}/courses/${courseId}/reorder_lessons`, { lesson_ids: lessonIds })
    return response.data
  }

  async enrollInCourse(courseCode: string, displayName?: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/public/course/${courseCode}/enroll`, { display_name: displayName })
    return response.data
  }

  async getCourseInfoPublic(courseCode: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/public/course/${courseCode}/info`)
    return response.data
  }

  // Self-paced enrollment (auth required, deducts credits)
  async enrollInCourseSelfPaced(courseCode: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/public/course/${courseCode}/enroll`)
    return response.data
  }

  // Instructor-led join (free, QR-based)
  async joinCourseLive(courseCode: string, displayName: string, sessionId?: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/public/course/${courseCode}/join_live`,
      { display_name: displayName },
      { headers: sessionId ? { 'X-Session-ID': sessionId } : {} }
    )
    return response.data
  }

  // Get course progress for self-paced enrollment
  async getCourseProgress(courseCode: string): Promise<ApiResponse<CourseProgress>> {
    const response = await this.client.get(`/public/course/${courseCode}/progress`)
    return response.data
  }

  // Advance to next lesson in self-paced course
  async advanceCourse(courseCode: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/public/course/${courseCode}/advance`)
    return response.data
  }

  // Admin: promote user to captain
  async promoteToCaptain(userId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/admin/captains/${userId}/promote`)
    return response.data
  }

  // Admin: demote captain to pirate
  async demoteCaptain(userId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/admin/captains/${userId}/demote`)
    return response.data
  }

  async getCaptains(params?: { search?: string; page?: number; per_page?: number }): Promise<ApiResponse<Array<{ id: string; email: string; display_name: string | null; avatar_url: string | null; role?: string; created_at?: string }>>> {
    const response = await this.client.get('/admin/captains', { params })
    return response.data
  }

  async getOnboardingData(params?: { search?: string; status?: string; page?: number; per_page?: number; sort_by?: string; sort_dir?: string }): Promise<ApiResponse<any>> {
    const response = await this.client.get('/admin/captains/onboarding_data', { params })
    return response.data
  }

  // Profile
  async updateProfile(data: { display_name: string }): Promise<ApiResponse<any>> {
    const response = await this.client.patch('/profile', data)
    return response.data
  }

  async completeProfileOnboarding(data: {
    country: string
    institution: string
    grades: string[]
    subjects: string[]
    using_potato_pirates: string
  }): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.post('/profile/complete_onboarding', data)
    return response.data
  }

  async completeTour(tourId: string): Promise<ApiResponse<{ tours_completed: string[]; tours_skipped: string[]; credit_balance: number }>> {
    const response = await this.client.post('/profile/complete_tour', { tour_id: tourId })
    return response.data
  }

  async skipTour(tourId: string): Promise<ApiResponse<{ tours_completed: string[]; tours_skipped: string[]; credit_balance: number }>> {
    const response = await this.client.post('/profile/skip_tour', { tour_id: tourId })
    return response.data
  }

  async changePassword(current_password: string, new_password: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/auth/password/change', { current_password, new_password })
    return response.data
  }

  async deleteAccount({ password, confirmation }: { password?: string; confirmation?: string }): Promise<ApiResponse<any>> {
    const response = await this.client.delete('/profile', { data: { password, confirmation } })
    return response.data
  }

  // Newsletter
  async getNewsletterStatus(): Promise<ApiResponse<{ subscribed: boolean }>> {
    const response = await this.client.get('/newsletter/status')
    return response.data
  }

  async subscribeNewsletter(): Promise<ApiResponse<{ subscribed: boolean }>> {
    const response = await this.client.post('/newsletter/subscribe')
    return response.data
  }

  async unsubscribeNewsletter(): Promise<ApiResponse<{ subscribed: boolean }>> {
    const response = await this.client.post('/newsletter/unsubscribe')
    return response.data
  }

  // Dashboard
  async getDashboard(): Promise<ApiResponse<DashboardData>> {
    const response = await this.client.get('/dashboard')
    return response.data
  }

  // Referrals
  async getReferralInfo(): Promise<ApiResponse<ReferralInfo>> {
    const response = await this.client.get('/referrals')
    return response.data
  }

  async submitReferralCode(referralCode: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.post('/referrals', { referral_code: referralCode })
    return response.data
  }

  // Leaderboard
  async getLeaderboard(category = 'missions'): Promise<ApiResponse<LeaderboardData>> {
    const response = await this.client.get(`/leaderboard?category=${category}`)
    return response.data
  }

  // ===== Question Bank =====

  async getQuestionBankQuestions(activityType: string, filters?: { category?: string; difficulty?: string }): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams({ activity_type: activityType })
    if (filters?.category) params.append('category', filters.category)
    if (filters?.difficulty) params.append('difficulty', filters.difficulty)
    const response = await this.client.get(`/question_banks?${params}`)
    return response.data
  }

  async getQuestionBankQuestion(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/question_banks/${id}`)
    return response.data
  }

  async getQuestionBankCategories(): Promise<ApiResponse<Record<string, { category: string; count: number }[]>>> {
    const response = await this.client.get('/question_banks/categories')
    return response.data
  }

  // ===== Cyber Card Glossary =====

  async getCyberCards(filters?: { game?: string; category?: string; q?: string }): Promise<ApiResponse<CyberCard[]>> {
    const params = new URLSearchParams({ game: filters?.game || 'spudnet' })
    if (filters?.category) params.append('category', filters.category)
    if (filters?.q) params.append('q', filters.q)
    const response = await this.client.get(`/cyber_cards?${params}`)
    return response.data
  }

  async getCyberCard(id: string): Promise<ApiResponse<CyberCard>> {
    const response = await this.client.get(`/cyber_cards/${id}`)
    return response.data
  }

  async getCyberCardCategories(game = 'spudnet'): Promise<ApiResponse<Record<string, { category: string; count: number }[]>>> {
    const response = await this.client.get(`/cyber_cards/categories?game=${game}`)
    return response.data
  }

  async createCyberCard(formData: FormData): Promise<ApiResponse<CyberCard>> {
    const response = await this.client.post('/cyber_cards', formData)
    return response.data
  }

  async updateCyberCard(id: string, formData: FormData): Promise<ApiResponse<CyberCard>> {
    const response = await this.client.patch(`/cyber_cards/${id}`, formData)
    return response.data
  }

  async deleteCyberCard(id: string): Promise<ApiResponse<null>> {
    const response = await this.client.delete(`/cyber_cards/${id}`)
    return response.data
  }

  // HIBP (authenticated)
  async hibpBreachCheck(email: string): Promise<ApiResponse<{ breaches: any[] }>> {
    const response = await this.client.post('/hibp/breach_check', { email })
    return response.data
  }

  async hibpPasswordRange(hashPrefix: string): Promise<ApiResponse<{ suffixes: string }>> {
    const response = await this.client.post('/hibp/password_range', { hash_prefix: hashPrefix })
    return response.data
  }

  // Generic request method for custom endpoints
  async request<T = any>(method: 'get' | 'post' | 'patch' | 'delete', url: string, data?: any): Promise<{ data: T; [key: string]: any }> {
    const response = await this.client.request({
      method,
      url,
      data,
    })
    return response.data
  }
}

// Singleton instance
export const apiClient = new ApiClient()

